import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts } from '../lib/api';
import type { BlogPost } from '../types';

/* ── helpers ───────────────────────────────────────── */

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3.6e6);
  if (h < 1) return 'Just now';
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}

/* ── types ─────────────────────────────────────────── */

interface Slide {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  hero_image: string;
  tags: string[];
  source_name: string;
  published_at: string;
}

/* ── component ─────────────────────────────────────── */

export default function NewsCarousel() {
  const [posts, setPosts] = useState<Slide[]>([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetchBlogPosts(undefined, 6).then((data: BlogPost[]) => {
      const slides = data.map(p => ({
        id: p.id,
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt,
        hero_image: p.hero_image,
        tags: p.tags,
        source_name: p.source_name || 'MarineLink',
        published_at: p.published_at,
      }));
      setPosts(slides);
      setLoaded(true);
    });
  }, []);

  const next = useCallback(() => {
    setCurrent(c => (c + 1) % Math.max(posts.length, 1));
  }, [posts.length]);

  const prev = useCallback(() => {
    setCurrent(c => (c - 1 + posts.length) % Math.max(posts.length, 1));
  }, [posts.length]);

  // Auto-advance
  useEffect(() => {
    if (paused || posts.length < 2) return;
    timerRef.current = setInterval(next, 6000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [paused, posts.length, next]);

  // Keyboard
 useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prev, next]);

  if (!loaded || posts.length === 0) return null;

  const SLIDE_DURATION = 6000;

  return (
    <section
      className="relative w-full overflow-hidden bg-[#061826]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-label="Marine news carousel"
    >
      {/* Slides */}
      <div className="relative h-[420px] sm:h-[480px] md:h-[540px]">
        {posts.map((post, i) => {
          const isActive = i === current;
          return (
            <div
              key={post.id}
              className={`absolute inset-0 transition-all duration-700 ease-out ${
                isActive ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 pointer-events-none z-0'
              }`}
              aria-hidden={!isActive}
            >
              {/* Background image - use <img> for reliable loading */}
              <img
                src={post.hero_image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              {/* Gradient overlays - lighter so images show through */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#04101a] via-[#04101a]/30 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#04101a]/60 via-transparent to-transparent" />

              {/* Content */}
              <div className="absolute inset-0 flex items-end">
                <div className="max-w-[1440px] mx-auto w-full px-4 md:px-8 pb-10 md:pb-14">
                  <div className="max-w-2xl">
                    {/* Tag pills */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.tags.slice(0, 2).map(tag => (
                        <span
                          key={tag}
                          className="text-[0.6rem] sm:text-[0.65rem] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full bg-accent/20 text-accent-bright border border-accent/30 backdrop-blur-sm"
                        >
                          {tag}
                        </span>
                      ))}
                      <span className="text-[0.6rem] sm:text-[0.65rem] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/10 text-white/70 border border-white/15 backdrop-blur-sm">
                        {post.source_name}
                      </span>
                    </div>

                    {/* Title */}
                    <Link to={`/news/${post.slug}`} className="block group">
                      <h2 className="font-display text-xl sm:text-2xl md:text-[2.2rem] font-bold leading-[1.15] tracking-tight text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)] group-hover:text-accent-bright transition-colors duration-300">
                        {post.title}
                      </h2>
                    </Link>

                    {/* Excerpt */}
                    <p className="hidden sm:block text-sm md:text-base text-white/75 mt-3 leading-relaxed line-clamp-2 max-w-[560px] drop-shadow">
                      {post.excerpt}
                    </p>

                    {/* Meta row */}
                    <div className="flex items-center gap-3 mt-4">
                      <Link
                        to={`/news/${post.slug}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-bright hover:text-white transition-colors"
                      >
                        Read story
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform group-hover:translate-x-0.5">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <span className="text-xs text-white/40">·</span>
                      <span className="text-xs text-white/50">{timeAgo(post.published_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-white/10 z-20">
        <div
          key={current + (paused ? 'p' : 'r')}
          className="h-full bg-accent-bright origin-left"
          style={{
            animation: paused ? 'none' : `newsProgress ${SLIDE_DURATION}ms linear forwards`,
          }}
        />
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-black/30 hover:bg-black/50 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-all"
        aria-label="Previous story"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        onClick={next}
        className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-9 h-9 md:w-11 md:h-11 rounded-full bg-black/30 hover:bg-black/50 border border-white/15 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white transition-all"
        aria-label="Next story"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* Dot indicators + section label */}
      <div className="absolute bottom-4 right-4 md:right-8 z-20 flex items-center gap-3">
        <Link
          to="/news"
          className="hidden sm:inline-flex text-[0.7rem] font-semibold uppercase tracking-wider text-white/60 hover:text-white transition-colors mr-2"
        >
          All News →
        </Link>
        <div className="flex gap-1.5">
          {posts.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === current ? 'w-6 bg-accent-bright' : 'w-1.5 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to story ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* "MARINE NEWS" label top-left */}
      <div className="absolute top-4 left-4 md:top-5 md:left-8 z-20 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-[0.65rem] sm:text-xs font-bold uppercase tracking-[0.15em] text-white/80">
          Marine News
        </span>
      </div>

      <style>{`
        @keyframes newsProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
      `}</style>
    </section>
  );
}
